'use client';

import { ANALYTICS_ROOT_DOMAIN, isAnalyticsHost } from '@/lib/analytics';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

// 네이버 애널리틱스 사이트 ID (analytics.naver.com 발급).
// 광고 전환 추적(naver-wcs.tsx, s_ 공통키)과 별개 계정이며 네이버 가이드상 함께 설치한다.
// 운영 도메인(지점 서브도메인 포함)에서만 집계한다(lib/analytics).
const NAVER_ANALYTICS_ID = '1b036ade8a75b90';

/**
 * wcslog 는 호출 시점의 wcs_add.wa 로 계정을 고른다. 광고 전환 스크립트(NaverWcs)와 같은 전역을
 * 쓰므로, 매 호출 직전에 이 계정을 넣고 같은 흐름에서 바로 호출한다.
 */
function runWithAccount(run: () => void): void {
  if (!window.wcs) return;
  window.wcs_add = window.wcs_add || {};
  window.wcs_add['wa'] = NAVER_ANALYTICS_ID;
  run();
}

/**
 * 네이버 애널리틱스 — 유입 검색어·유입 경로·페이지뷰.
 *
 * - 유입분석(검색어·이전 사이트)은 `wcs.inflow()`가 맡고 `wcs_do()`는 페이지뷰 전용이라 둘 다 부른다.
 *   inflow 는 referrer 기반이라 문서당 한 번만 부른다(SPA 이동에서 다시 부르면 같은 유입이 또 잡힌다).
 * - SPA 이동에서는 스크립트가 다시 실행되지 않으므로 경로가 바뀔 때 `wcs_do()`를 다시 부른다.
 * - 운영 도메인 여부는 서버가 모르므로 마운트 후에만 켠다(하이드레이션 불일치 방지).
 *   useSearchParams 는 트리를 CSR 로 바꾸므로 쓰지 않는다.
 */
export default function NaverAnalytics() {
  const pathname = usePathname();
  const loadedRef = useRef(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setEnabled(isAnalyticsHost());
  }, []);

  useEffect(() => {
    if (!loadedRef.current) return;
    runWithAccount(() => window.wcs_do?.());
  }, [pathname]);

  if (!enabled) return null;

  return (
    <Script
      id="naver-analytics-wcslog"
      src="https://wcs.pstatic.net/wcslog.js"
      strategy="afterInteractive"
      onLoad={() => {
        loadedRef.current = true;
        runWithAccount(() => {
          window.wcs?.inflow(ANALYTICS_ROOT_DOMAIN);
          window.wcs_do?.();
        });
      }}
    />
  );
}
